import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Crown, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "bg-[#0B0F19] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-sm text-starlight outline-none focus:border-[#D4AF37]";
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");
const err = (e) => toast.error(e.response?.data?.detail || "Something went wrong");
const EMPTY = { name: "", email: "", months: 1, note: "" };

function status(p) {
  if (p.status === "claimed") return `Claimed ${day(p.claimed_at)} · Premium until ${day(p.ends_at)}${p.reminded_at ? " · reminder sent" : ""}`;
  if (p.status === "revoked") return "Cancelled";
  if (p.send_error && !p.sent_at) return "Email failed to send · use Resend";
  return p.sent_at ? `Emailed ${day(p.sent_at)} · not claimed yet` : "Sending…";
}

export default function PremiumPasses({ token }) {
  const h = { headers: { "X-Analytics-Token": token } };
  const [rows, setRows] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/analytics/passes`, { headers: { "X-Analytics-Token": token } }).then(({ data }) => setRows(data.passes)).catch(err), [token]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API}/analytics/passes`, { ...f, months: Number(f.months) }, h); toast.success(`Pass emailed to ${f.email}`); setF(EMPTY); setTimeout(load, 1500); } catch (e2) { err(e2); }
    setBusy(false);
  };
  const act = (p, what, ok) => axios.post(`${API}/analytics/passes/${p.id}/${what}`, {}, h).then(() => { toast.success(ok); load(); }).catch(err);
  return (
    <section className="mb-10" data-testid="premium-passes-section">
      <h2 className="text-lg text-starlight mb-1 flex items-center gap-2"><Crown size={18} className="gold-text" /> Premium passes</h2>
      <p className="text-sm text-[#A7B0C0] mb-4">Give collaborators or selected people free Premium. They get an email link and must sign in with that same email to claim it. Nothing renews, and they're reminded 3 days before it ends.</p>
      <form onSubmit={send} data-testid="pass-form" className="bg-[#0F1523] border border-[#D4AF37]/30 rounded-2xl p-4 grid gap-3 sm:grid-cols-6 items-end">
        <label className="text-xs text-[#A7B0C0] sm:col-span-1">Name<input data-testid="pass-name" required maxLength={60} value={f.name} onChange={set("name")} className={`${field} w-full mt-1`} /></label>
        <label className="text-xs text-[#A7B0C0] sm:col-span-2">Email<input data-testid="pass-email" type="email" required value={f.email} onChange={set("email")} className={`${field} w-full mt-1`} /></label>
        <label className="text-xs text-[#A7B0C0]">Months
          <select data-testid="pass-months" value={f.months} onChange={set("months")} className={`${field} w-full mt-1`}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#A7B0C0] sm:col-span-2">Personal note (optional)<input data-testid="pass-note" maxLength={300} value={f.note} onChange={set("note")} className={`${field} w-full mt-1`} /></label>
        <button data-testid="pass-send" disabled={busy} className="sm:col-span-6 sm:justify-self-start flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium rounded-full px-6 py-2.5 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send free Premium
        </button>
      </form>
      <div className="mt-4 space-y-2" data-testid="pass-list">
        {!rows ? <Loader2 className="animate-spin gold-text" size={18} /> : rows.length === 0 ? <p className="text-sm text-[#A7B0C0]">No passes sent yet.</p> : rows.map((p) => (
          <div key={p.id} data-testid={`pass-row-${p.id}`} className="flex flex-wrap items-center gap-3 bg-[#0F1523] border border-[#D4AF37]/15 rounded-xl px-4 py-3 text-sm">
            <span className="flex-1 min-w-[200px]"><span className="text-starlight">{p.name}</span> <span className="text-[#A7B0C0]">· {p.email} · {p.months} mo</span>
              <span className="block text-xs text-[#A7B0C0]" data-testid={`pass-status-${p.email}`}>{status(p)}</span></span>
            {p.status === "sent" && <>
              <button data-testid={`pass-resend-${p.email}`} onClick={() => act(p, "resend", "Email resent")} className="text-xs gold-text">Resend</button>
              <button data-testid={`pass-revoke-${p.email}`} onClick={() => act(p, "revoke", "Pass cancelled")} className="text-xs text-[#A7B0C0] hover:text-red-300">Cancel</button>
            </>}
          </div>
        ))}
      </div>
    </section>
  );
}
